<template>
  <div class="osk-products-filters-categories">
    <UikitList
      :title="title || value?.name"
      class="osk-products-filters-categories__list"
    >
      <UikitListItem
        v-if="value && (value?.children?.length ?? 0) > 1"
        class="osk-products-filters-categories__item"
        no-padding
      >
        <UikitFormCheckbox
          :model-value="isAll"
          name="categories"
          round
          class="osk-products-filters-categories__checkbox"
          @update:model-value="handleIsAll"
        >
          {{ $t('components.business.products.filters.all') }}
        </UikitFormCheckbox>
      </UikitListItem>

      <UikitListItem
        v-for="item in filterInfo?.values || value?.children"
        :key="item.id"
        class="osk-products-filters-categories__item"
        no-padding
        @click="handleClick(item)"
      >
        <template v-if="item.children">
          <span>
            {{ item.name }}
          </span>

          <UikitLabel
            :text="getLabel(item)"
            class="osk-products-filters-categories__label"
            small
            bold
          />

          <UikitIcon
            name="ArrowRightMark"
            class="osk-products-filters-categories__arrow"
          />
        </template>

        <UikitFormCheckbox
          v-else
          v-model="categories"
          name="categories"
          :value="genItem(item)"
          round
          class="osk-products-filters-categories__checkbox"
          @update:model-value="handleCheckboxClick(item)"
        >
          {{ item.name }}
        </UikitFormCheckbox>
      </UikitListItem>
    </UikitList>

    <UikitDialog
      v-model="showDialog"
      full-height
      position="right"
      :hide-close="!isDesktop"
      content-class="osk-products-filters-categories-dialog"
      slide-right
      @handle-close="handleClose"
    >
      <div class="osk-products-filters-categories-dialog__wrapper">
        <div class="osk-products-filters-categories-dialog__header">
          <UikitIcon
            name="ArrowBack"
            size="l"
            @click="showDialog = false"
          />

          <div
            class="osk-products-filters-categories-dialog__refresh"
            @click="handleReset"
          >
            {{ $t('components.business.products.filters.resetAll') }}
          </div>
        </div>

        <div class="osk-products-filters-dialog__content">
          <BusinessProductsFiltersCategories
            :value="newValue"
            :selected-values="categories"
            :items-count="itemsCount"
            :loading="loading"
            @update-filter="handleUpdate"
            @close-all="handleClose"
            @apply="handleApply"
          />
        </div>

        <div class="osk-products-filters-categories-dialog__footer">
          <UikitButton
            full-width
            :disabled="!itemsCount"
            :loading="loading"
            :size="isDesktop ? 'm' : 'l'"
            @click="handleApply"
          >
            {{ $t('components.business.products.filters.show') }}
            <template v-if="itemsCount">
              ({{ itemsCount }})
            </template>
          </UikitButton>
        </div>
      </div>
    </UikitDialog>
  </div>
</template>

<script lang="ts" setup>
import {
  CategoryTreeFilter, ListValue, CategoryTreeValue,
} from '~/restAPI/data-contracts';
import { AnalyticsEvents } from '~/types/analytics';

const { $addEvent } = useNuxtApp();

const props = defineProps<{
  filterInfo?: CategoryTreeFilter
  title?: string
  loading?: boolean
  value?: CategoryTreeValue
  itemsCount?: number
  selectedValues?: ListValue[]
  page?: string
}>();

const emits = defineEmits<{
  (e: 'updateFilter', value?: ListValue[]): void,
  (e: 'closeAll'): void,
  (e: 'apply'): void,
}>();

const categories = ref<ListValue[]>(genSelected());
const showDialog = ref(false);
const newValue = ref<CategoryTreeValue>();

const isAll = computed(() => genIsAll());

const { isDesktop } = useUiBreakpoints();

function handleCheckboxClick(item: CategoryTreeValue) {
  $addEvent(AnalyticsEvents.FiltersLinkClick, {
    chapter: 'category',
    // eslint-disable-next-line no-param-reassign
    id_chapter: categories.value.map((val) => val.id).reduce((result, id) => result += `${id};`, '').slice(0, -1),
    id_item: String(item.id),
    page: props.page,
  });
}

function handleClose() {
  emits('closeAll');
}

function handleApply() {
  showDialog.value = false;
  emits('apply');
}

function getChild(items?: CategoryTreeValue[], value: ListValue): CategoryTreeValue | undefined {
  return items?.find((item) => {
    if (item.id === value.id) return item;

    return getChild(item.children, value);
  });
}

function getLabel(item: CategoryTreeValue) {
  const children = categories.value.reduce((result, category) => {
    const child = getChild(item.children, category);

    if (child) {
      result.push(category);
    }

    return result;
  }, [] as ListValue[]);

  return children.length > 1 ? String(children.length) : (children[0]?.value ?? '');
}

function genSelected() {
  return props.filterInfo?.selectedValues ?? props.selectedValues ?? [];
}

function genItem({ id, name }: CategoryTreeValue) {
  return { id, value: name };
}

function handleClick(item: CategoryTreeValue) {
  if (!item.children) return;

  newValue.value = item;
  showDialog.value = true;
}

function handleUpdate(list?: ListValue[]) {
  categories.value = list ?? [];
}

function handleReset() {
  categories.value = categories.value.filter((category) => {
    const child = getChild(newValue.value?.children, category);

    return !child;
  });
}

function getAllChildren(items: CategoryTreeValue[]): ListValue[] {
  return items.reduce((result, item) => {
    if (!item.children) {
      result.push({ id: item.id, value: item.name });
    } else {
      result.push(...getAllChildren(item.children));
    }

    return result;
  }, [] as ListValue[]);
}

function handleIsAll(value: boolean) {
  if (value) {
    const children = getAllChildren(props.value?.children ?? []);

    const filteredCategories = categories.value.filter((category) => {
      const child = children.find((c) => c.id === category.id);

      return !child;
    });

    categories.value = [ ...filteredCategories, ...children ];

    $addEvent(AnalyticsEvents.FiltersLinkClick, {
      chapter: 'category',
      // eslint-disable-next-line no-param-reassign
      id_chapter: categories.value.map((val) => val.id).reduce((result, id) => result += `${id};`, '').slice(0, -1),
      id_item: 'all',
      page: props.page,
    });

    return;
  }

  categories.value = categories.value.filter((category) => {
    const child = getChild(props.value?.children, category);

    return !child;
  });

  $addEvent(AnalyticsEvents.FiltersLinkClick, {
    chapter: 'category',
    // eslint-disable-next-line no-param-reassign
    id_chapter: categories.value.map((val) => val.id).reduce((result, id) => result += `${id};`, '').slice(0, -1),
    id_item: 'all',
    page: props.page,
  });
}

function getIsAll(items: CategoryTreeValue[]): boolean {
  return items.every((item) => {
    if (!item.children) {
      return !!categories.value.find((c) => c.id === item.id);
    }

    return getIsAll(item.children);
  });
}

function genIsAll() {
  const check = getIsAll(props.value?.children ?? []);

  return !!check;
}

watch(categories, (list) => {
  emits('updateFilter', list);
});

watch(() => props.filterInfo, () => {
  categories.value = genSelected();
});

watch(() => props.selectedValues, () => {
  categories.value = genSelected();
});
</script>

<style lang="scss">
@import "~/assets/scss/settings/index";

.osk-products-filters-categories {
  &__item {
    height: 52px;
    display: flex;
    align-items: center;
    padding: 0 16px !important;
  }

  &__checkbox {
    height: 52px;
    width: 100%;
    cursor: pointer;
  }

  &__label {
    margin-left: auto;

    & + .osk-products-filters-categories__arrow {
      margin-left: $indent-compact;
    }
  }

  &__arrow {
    margin-left: auto;
    margin-right: -5px;
  }

  .osk-list__title {
    font-weight: 700 !important;
    font-size: 22px !important;
    line-height: 27px !important;

    @include media-query(lg-and-up) {
      font-weight: $font-weight-bold !important;
      font-size: $font-size-heading !important;
      line-height: $line-height-heading !important;
    }
  }
}

.osk-products-filters-categories-dialog {
  padding-top: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;

  @include media-query(lg-and-up) {
    width: 504px !important;
  }

  .osk-dialog__close {
    right: 50px !important;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;

    @include media-query(lg-and-up) {
      padding-top: 94px;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: relative;
    padding: 10px 16px;

    @include media-query(lg-and-up) {
      padding: 0 52px;
    }

    .osk-icon {
      cursor: pointer;
      margin-left: -6px;

      &:hover {
        opacity: .6;
      }
    }
  }

  &__refresh {
    color: $grey;
    cursor: pointer;

    @include media-query(lg-and-up) {
      margin-left: auto;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    height: 100%;
    padding: 22px $indent-mmedium 0;
    position: relative;

    @include media-query(lg-and-up) {
      padding: 26px 52px 0;
    }
  }

  &__footer {
    padding: $indent-medium $indent-medium 0;

    @include media-query(lg-and-up) {
      padding: $indent-medium 52px 22px;
    }
  }
}
</style>
